// 원장 호출 — 진료실 격자 (행 = 체어, 열 = 상황 4종). 라이트 고정·상황색 고정·글자 입력 0 (명세 §2)
//  - 칸 한 번 = 호출, 켜진 칸 한 번 = 해제. 켜진 칸: "대기 m:ss" / 응답 시 "원장 오는 중" 또는 "2분 (1:23)" / 자동 해제 후 1분 "지남" 회색
//  - 미응답 soundUpSec 초 → 칸 깜빡임(1초 간격). 공용 화면은 소리를 내지 않는다
//  - 연결 끊김이면 모든 칸 비활성(누를 수 없음) — 상단 회색 띠는 부모가 그린다
//  - compact = 접수 화면 3번째 카드 안(칸 최소 48px), 아니면 전체 화면 크기(4대 이하 104 / 5~8대 88 / 9대 이상 2열)
import React from 'react';
import { SITUATION_KEYS, situationColor, situationLabel, toMs, fmtMSS, ackRemainMs, ackLabel, gridLayout } from '../../../utils/doctorCall';

const EXPIRED_SHOW_MS = 60000;

// 한 칸에 해당하는 호출 찾기 — 진행 중 우선, 없으면 1분 안에 자동 해제된 것
function findCell(calls, chairNo, situation, nowMs) {
    let expired = null;
    for (const c of calls) {
        if (c.chairNo !== chairNo || c.situation !== situation) continue;
        if (c.state === 'open' || c.state === 'acked') return { kind: 'live', call: c };
        if (c.state === 'expired' && c.endReason === 'ack_elapsed') {
            const at = toMs(c.clearedAt);
            if (Number.isFinite(at) && nowMs - at < EXPIRED_SHOW_MS) expired = c;
        }
    }
    return expired ? { kind: 'expired', call: expired } : null;
}

// 누르는 동안 반투명·비활성 없음(10-08) — 화면은 Firestore 로컬 반영으로 즉시 바뀌고, 연타는 useDoctorCall 의 in-flight 가드가 무시한다
function DoctorCallGrid({ settings, calls, nowMs, disabled, compact, onCell }) {
    const n = settings.chairs;
    // 카드 안(compact, 폭 340~460px)은 체어 수와 무관하게 1열 세로 스크롤 — 2열은 전체 화면에서만(가로 넘침 방지)
    const { cell, cols } = compact ? { cell: 48, cols: 1 } : gridLayout(n);
    const chairs = Array.from({ length: n }, (_, i) => i + 1);
    const perCol = Math.ceil(n / cols);
    const groups = Array.from({ length: cols }, (_, g) => chairs.slice(g * perCol, (g + 1) * perCol));
    const fz = compact ? 12.5 : (cell >= 104 ? 19 : 17);
    const subFz = compact ? 11.5 : (cell >= 104 ? 16 : 14);

    const renderCell = (chairNo, sit) => {
        const color = situationColor(sit, settings);
        const hit = findCell(calls, chairNo, sit, nowMs);
        const key = `${chairNo}_${sit}`;
        let style = { ...G.cell, minHeight: cell, borderColor: color, color };
        let main = situationLabel(sit, settings);
        let sub = '';
        if (hit?.kind === 'live') {
            const c = hit.call;
            style = { ...style, background: color, color: '#fff' };
            if (c.state === 'acked' && c.ack?.type) {
                const remain = ackRemainMs(c, nowMs);
                sub = remain === null ? '원장 오는 중' : `${ackLabel(c)} (${fmtMSS(remain)})`;
            } else {
                const waited = nowMs - toMs(c.createdAt);
                sub = `대기 ${fmtMSS(Number.isFinite(waited) ? waited : 0)}`;
                // 미응답 soundUpSec 경과 → 1초 간격 깜빡임
                if (Number.isFinite(waited) && waited >= settings.soundUpSec * 1000 && Math.floor(waited / 1000) % 2 === 0) {
                    style = { ...style, background: '#fff', color, boxShadow: `inset 0 0 0 3px ${color}` };
                }
            }
        } else if (hit?.kind === 'expired') {
            style = { ...style, background: '#e5e7eb', borderColor: '#9ca3af', color: '#4b5563' };
            sub = '지남';
        }
        if (disabled) style = { ...style, opacity: 0.45, cursor: 'not-allowed' };
        return (
            <button key={key} type="button" disabled={disabled}
                onClick={() => onCell(chairNo, sit, hit)}
                aria-label={`${chairNo}번 체어 ${situationLabel(sit, settings)}${sub ? ` ${sub}` : ''}`}
                style={style}>
                <span style={{ fontSize: fz, fontWeight: 800, lineHeight: 1.2 }}>{main}</span>
                {sub && <span style={{ fontSize: subFz, fontWeight: 700, fontFamily: G.mono, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{sub}</span>}
            </button>
        );
    };

    // 설정 체어 수보다 큰 번호의 열린 호출(체어 수를 줄인 뒤 남은 것) — 격자에는 칸이 없으므로 따로 [끄기] 줄로 보인다(계약 §4-1: ③은 체어 수 검사 없음)
    const extra = calls.filter(c => c.chairNo > n && (c.state === 'open' || c.state === 'acked'));

    return (
        <div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: compact ? 8 : 16 }}>
            {groups.map((grp, gi) => (
                <div key={gi} style={{ display: 'grid', gridTemplateColumns: `${compact ? 44 : 72}px repeat(4, minmax(0, 1fr))`, gap: compact ? 4 : 8, alignItems: 'stretch' }}>
                    {grp.map(chairNo => (
                        <React.Fragment key={chairNo}>
                            <div style={{ ...G.chair, fontSize: compact ? 13 : (cell >= 104 ? 22 : 19) }}>{`${chairNo}번`}</div>
                            {SITUATION_KEYS.map(sit => renderCell(chairNo, sit))}
                        </React.Fragment>
                    ))}
                </div>
            ))}
        </div>
        {extra.length > 0 && (
            <div style={{ marginTop: compact ? 8 : 14, border: '1px dashed #9ca3af', borderRadius: 8, padding: '8px 10px', display: 'grid', gap: 6 }}>
                <div style={{ fontSize: 12.5, color: '#4b5563', fontWeight: 700 }}>{`설정 체어 수(${n}대)보다 큰 번호의 켜진 호출`}</div>
                {extra.map(c => (
                    <div key={c.cellId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: situationColor(c.situation, settings) }}>{`${c.chairNo}번 체어 · ${situationLabel(c.situation, settings)}`}</span>
                        <button type="button" disabled={disabled}
                            onClick={() => onCell(c.chairNo, c.situation, { kind: 'live', call: c })}
                            style={{ marginLeft: 'auto', minHeight: 40, minWidth: 64, border: '1px solid #9ca3af', background: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                            {'끄기'}
                        </button>
                    </div>
                ))}
            </div>
        )}
        </div>
    );
}

const G = {
    mono: 'ui-monospace, Menlo, "IBM Plex Mono", monospace',
    cell: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, minWidth: 48, padding: '4px 6px', border: '2px solid', borderRadius: 10, background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', touchAction: 'manipulation', userSelect: 'none', WebkitTapHighlightColor: 'transparent' },
    chair: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#17201d', background: '#f5f7f6', border: '1px solid #d9dfdc', borderRadius: 10 },
};

export default DoctorCallGrid;
